import { useState, useEffect } from 'react';
import { TopBar } from '../../components/feature/TopBar';
import { Sidebar } from '../../components/feature/Sidebar';

export default function HistoryPage() {
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('energy');
  const [chartType, setChartType] = useState('line');

  // Simular datos históricos
  useEffect(() => {
    const fetchHistoricalData = () => {
      setIsLoading(true);
      setTimeout(() => {
        const days = selectedPeriod === 'week' ? 7 : selectedPeriod === 'month' ? 30 : 365;
        const data = [];

        for (let i = days - 1; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);

          data.push({
            date: date.toISOString().split('T')[0],
            timestamp: date.getTime(),
            energyProduced: Math.floor(Math.random() * 30) + 20,
            energyConsumed: Math.floor(Math.random() * 25) + 15,
            maxPower: Math.floor(Math.random() * 3000) + 1000,
            avgEfficiency: Math.floor(Math.random() * 10) + 90,
            temperature: Math.floor(Math.random() * 20) + 35,
            gridExport: Math.floor(Math.random() * 15) + 5,
            gridImport: Math.floor(Math.random() * 10) + 2,
            autonomy: Math.floor(Math.random() * 30) + 70,
            selfConsumption: Math.floor(Math.random() * 20) + 80
          });
        }

        setHistoricalData(data);
        setLastUpdate(new Date());
        setIsLoading(false);
      }, 1500);
    };

    fetchHistoricalData();
  }, [selectedPeriod]);

  const getMetricData = () => {
    switch (selectedMetric) {
      case 'energy':
        return {
          title: 'Energía (kWh)',
          datasets: [
            { label: 'Producida', data: historicalData.map(d => d.energyProduced), color: 'rgb(34, 197, 94)' },
            { label: 'Consumida', data: historicalData.map(d => d.energyConsumed), color: 'rgb(59, 130, 246)' }
          ]
        };
      case 'power':
        return {
          title: 'Potencia Máxima (W)',
          datasets: [
            { label: 'Potencia Máxima', data: historicalData.map(d => d.maxPower), color: 'rgb(168, 85, 247)' }
          ]
        };
      case 'efficiency':
        return {
          title: 'Eficiencia (%)',
          datasets: [
            { label: 'Eficiencia Promedio', data: historicalData.map(d => d.avgEfficiency), color: 'rgb(245, 158, 11)' }
          ]
        };
      case 'grid':
        return {
          title: 'Intercambio con Red (kWh)',
          datasets: [
            { label: 'Exportación', data: historicalData.map(d => d.gridExport), color: 'rgb(34, 197, 94)' },
            { label: 'Importación', data: historicalData.map(d => d.gridImport), color: 'rgb(239, 68, 68)' }
          ]
        };
      default:
        return { title: '', datasets: [] };
    }
  };

  const calculateTotals = () => {
    const totalProduced = historicalData.reduce((sum, d) => sum + d.energyProduced, 0);
    const totalConsumed = historicalData.reduce((sum, d) => sum + d.energyConsumed, 0);
    const avgEfficiency = historicalData.reduce((sum, d) => sum + d.avgEfficiency, 0) / historicalData.length;
    const avgAutonomy = historicalData.reduce((sum, d) => sum + d.autonomy, 0) / historicalData.length;

    return { totalProduced, totalConsumed, avgEfficiency, avgAutonomy };
  };

  const getMaxValue = (datasets: any[]) => {
    const allValues = datasets.flatMap(dataset => dataset.data);
    return Math.max(...allValues);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64">
          <TopBar title="Datos Históricos" lastUpdate={lastUpdate} />
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando datos históricos...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metricData = getMetricData();
  const totals = calculateTotals();
  const maxValue = getMaxValue(metricData.datasets);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Datos Históricos" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1543185377-b757b70b56b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Análisis Histórico</h1>
                <p className="text-blue-100 max-w-xl">
                  Explora el rendimiento de tu sistema solar a lo largo del tiempo.
                </p>
              </div>
              <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-md p-2 rounded-xl border border-white/20">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="bg-transparent border-none text-white text-sm font-medium focus:ring-0 cursor-pointer"
                >
                  <option value="week" className="text-gray-900">Última Semana</option>
                  <option value="month" className="text-gray-900">Último Mes</option>
                  <option value="year" className="text-gray-900">Último Año</option>
                </select>
                <div className="h-6 w-px bg-white/20"></div>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="bg-transparent border-none text-white text-sm font-medium focus:ring-0 cursor-pointer"
                >
                  <option value="energy" className="text-gray-900">Energía</option>
                  <option value="power" className="text-gray-900">Potencia</option>
                  <option value="efficiency" className="text-gray-900">Eficiencia</option>
                  <option value="grid" className="text-gray-900">Red Eléctrica</option>
                </select>
                <div className="h-6 w-px bg-white/20"></div>
                <div className="flex bg-black/20 rounded-lg p-1">
                  <button
                    onClick={() => setChartType('line')}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${chartType === 'line'
                        ? 'bg-white text-blue-900 shadow-sm'
                        : 'text-white/70 hover:text-white'
                      }`}
                  >
                    <i className="ri-line-chart-line"></i>
                  </button>
                  <button
                    onClick={() => setChartType('bar')}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${chartType === 'bar'
                        ? 'bg-white text-blue-900 shadow-sm'
                        : 'text-white/70 hover:text-white'
                      }`}
                  >
                    <i className="ri-bar-chart-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen de Totales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-leaf-line text-green-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full border border-green-100">TOTAL</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Energía Producida</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{totals.totalProduced.toFixed(1)}</p>
                <p className="text-sm font-medium text-gray-400">kWh</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-home-line text-blue-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">TOTAL</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Energía Consumida</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{totals.totalConsumed.toFixed(1)}</p>
                <p className="text-sm font-medium text-gray-400">kWh</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-purple-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-settings-line text-purple-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">PROM</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Eficiencia Promedio</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{totals.avgEfficiency.toFixed(1)}</p>
                <p className="text-sm font-medium text-gray-400">%</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-orange-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-battery-charge-line text-orange-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-orange-700 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-100">PROM</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Autonomía Promedio</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{totals.avgAutonomy.toFixed(1)}</p>
                <p className="text-sm font-medium text-gray-400">%</p>
              </div>
            </div>
          </div>

          {/* Gráfico Principal */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{metricData.title}</h3>
                <p className="text-sm text-gray-500">Visualización de datos en el periodo seleccionado</p>
              </div>
              <div className="flex items-center space-x-4">
                {metricData.datasets.map((dataset, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm"
                      style={{ backgroundColor: dataset.color }}
                    ></div>
                    <span className="text-xs font-medium text-gray-700">{dataset.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-80 relative">
              {chartType === 'line' ? (
                <div className="h-full flex items-end space-x-1">
                  {historicalData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center space-y-2 group relative">
                      <div className="flex-1 flex items-end space-x-1 w-full justify-center">
                        {metricData.datasets.map((dataset, datasetIndex) => (
                          <div
                            key={datasetIndex}
                            className="w-full max-w-[8px] rounded-t transition-all duration-300 hover:opacity-80 relative"
                            style={{
                              height: `${(dataset.data[index] / maxValue) * 100}%`,
                              backgroundColor: dataset.color
                            }}
                          >
                            <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs py-1 px-2 rounded pointer-events-none whitespace-nowrap z-10 transition-opacity">
                              {dataset.data[index].toFixed(1)}
                            </div>
                          </div>
                        ))}
                      </div>
                      <span className="text-[10px] text-gray-400 transform -rotate-45 origin-top-left mt-2 opacity-0 group-hover:opacity-100 transition-opacity absolute top-full left-1/2">
                        {new Date(data.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex items-end space-x-2">
                  {historicalData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center space-y-2 group relative">
                      <div className="flex-1 flex items-end justify-center space-x-1 w-full">
                        {metricData.datasets.map((dataset, datasetIndex) => (
                          <div
                            key={datasetIndex}
                            className="w-full max-w-[16px] rounded-t transition-all duration-300 hover:opacity-80 relative"
                            style={{
                              height: `${(dataset.data[index] / maxValue) * 100}%`,
                              backgroundColor: dataset.color
                            }}
                          >
                            <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs py-1 px-2 rounded pointer-events-none whitespace-nowrap z-10 transition-opacity">
                              {dataset.data[index].toFixed(1)}
                            </div>
                          </div>
                        ))}
                      </div>
                      <span className="text-[10px] text-gray-400 transform -rotate-45 origin-top-left mt-2 opacity-0 group-hover:opacity-100 transition-opacity absolute top-full left-1/2">
                        {new Date(data.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tabla de Datos */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Datos Detallados</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="text-left py-4 px-6 font-semibold text-gray-500">Fecha</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-500">Producida (kWh)</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-500">Consumida (kWh)</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-500">Balance (kWh)</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-500">Eficiencia (%)</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-500">Autonomía (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {historicalData.slice(-10).reverse().map((data, index) => {
                    const balance = data.energyProduced - data.energyConsumed;
                    return (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6 text-gray-900 font-medium">
                          {new Date(data.date).toLocaleDateString('es-ES', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="py-4 px-6 text-right text-green-600 font-bold">
                          {data.energyProduced.toFixed(1)}
                        </td>
                        <td className="py-4 px-6 text-right text-blue-600 font-bold">
                          {data.energyConsumed.toFixed(1)}
                        </td>
                        <td className={`py-4 px-6 text-right font-bold ${balance >= 0 ? 'text-teal-600' : 'text-red-600'
                          }`}>
                          {balance >= 0 ? '+' : ''}{balance.toFixed(1)}
                        </td>
                        <td className="py-4 px-6 text-right text-purple-600 font-medium">
                          {data.avgEfficiency.toFixed(1)}
                        </td>
                        <td className="py-4 px-6 text-right text-orange-600 font-medium">
                          {data.autonomy.toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Análisis y Tendencias */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Análisis de Tendencias</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-center space-x-4">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <i className="ri-trending-up-line text-green-600 text-xl"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Producción Solar</span>
                  </div>
                  <span className="text-green-700 font-bold bg-green-100 px-2 py-1 rounded-lg text-sm">+12.5%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <i className="ri-trending-down-line text-blue-600 text-xl"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Consumo Energético</span>
                  </div>
                  <span className="text-blue-700 font-bold bg-blue-100 px-2 py-1 rounded-lg text-sm">-3.2%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="flex items-center space-x-4">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <i className="ri-trending-up-line text-purple-600 text-xl"></i>
                    </div>
                    <span className="text-gray-900 font-medium">Eficiencia Sistema</span>
                  </div>
                  <span className="text-purple-700 font-bold bg-purple-100 px-2 py-1 rounded-lg text-sm">+5.8%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Mejores Días</h3>
              <div className="space-y-3">
                {historicalData
                  .sort((a, b) => (b.energyProduced - b.energyConsumed) - (a.energyProduced - a.energyConsumed))
                  .slice(0, 5)
                  .map((data, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-green-200 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="bg-white w-10 h-10 rounded-lg flex items-center justify-center shadow-sm font-bold text-gray-400">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">
                            {new Date(data.date).toLocaleDateString('es-ES', {
                              weekday: 'long',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-gray-500 font-medium">
                            Producción: {data.energyProduced.toFixed(1)} kWh
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                          +{(data.energyProduced - data.energyConsumed).toFixed(1)} kWh
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}